import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Plus, PencilSimple, Trash, PaperPlaneTilt, ArrowsClockwise } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";

const emptyForm = { code: "", type: "percent", value: "10", valid_from: "", valid_to: "", max_redemptions: "", scope: "unlimited", email: "", active: true };

const STATUS = {
  active: { label: "Aktiv", color: "#16A34A" },
  inactive: { label: "Inaktiv", color: "#6B7280" },
  expired: { label: "Abgelaufen", color: "#DC2626" },
  scheduled: { label: "Geplant", color: "#D97706" },
};

export function DiscountAdmin() {
  const { t } = useApp();
  const [enabled, setEnabled] = useState(false);
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [sendOpen, setSendOpen] = useState(null);
  const [emails, setEmails] = useState("");

  const load = async () => {
    try {
      const [s, l] = await Promise.all([api.get("/admin/shop/discount/settings"), api.get("/admin/shop/discounts")]);
      setEnabled(s.data.enabled); setList(l.data);
    } catch (e) { toast.error("Fehler beim Laden"); }
  };
  useEffect(() => { load(); }, []);

  const toggle = async (v) => { setEnabled(v); try { await api.put("/admin/shop/discount/settings", { enabled: v }); } catch { setEnabled(!v); toast.error("Fehler"); } };

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (d) => {
    setEditing(d);
    setForm({ code: d.code, type: d.type, value: String(d.value), valid_from: d.valid_from || "", valid_to: d.valid_to || "", max_redemptions: d.max_redemptions != null ? String(d.max_redemptions) : "", scope: d.scope, email: d.email || "", active: !!d.active });
    setOpen(true);
  };

  const save = async () => {
    if (form.scope === "single" && !form.email.trim()) { toast.error("Bitte E-Mail für Einmalnutzung angeben"); return; }
    setBusy(true);
    try {
      const payload = {
        code: form.code.trim(), type: form.type, value: parseFloat(form.value) || 0,
        valid_from: form.valid_from || null, valid_to: form.valid_to || null,
        max_redemptions: form.max_redemptions.trim() === "" ? null : (parseInt(form.max_redemptions) || null),
        scope: form.scope, email: form.email.trim(), active: form.active,
      };
      if (editing) await api.put(`/admin/shop/discounts/${editing.id}`, payload);
      else await api.post("/admin/shop/discounts", payload);
      toast.success(t("saved")); setOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const remove = async (d) => { if (!window.confirm("Diesen Rabattcode löschen?")) return; await api.delete(`/admin/shop/discounts/${d.id}`); await load(); };

  const doSend = async () => {
    const arr = emails.split(/[,;\n\s]+/).map((x) => x.trim()).filter(Boolean);
    if (!arr.length) { toast.error("Bitte mindestens eine E-Mail angeben"); return; }
    setBusy(true);
    try { const r = await api.post(`/admin/shop/discounts/${sendOpen.id}/send`, { emails: arr }); toast.success(`${r.data.sent} E-Mail(s) gesendet`); setSendOpen(null); setEmails(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const fmtVal = (d) => d.type === "percent" ? `${d.value}%` : `${Number(d.value).toFixed(2)} €`;

  return (
    <div className="sb-card p-5 mt-6" data-testid="discount-admin">
      <div className="flex items-center gap-2 mb-3">
        <h2 className="text-lg font-heading font-semibold text-gray-900 flex items-center gap-2"><Ticket size={20} weight="bold" />Rabattcodes</h2>
        <div className="ml-auto flex items-center gap-2">
          <span className="font-body text-xs text-gray-500">Feld im Checkout</span>
          <Switch data-testid="discount-enabled-toggle" checked={enabled} onCheckedChange={toggle} />
        </div>
      </div>

      <button data-testid="new-discount-btn" onClick={openNew} className="sb-btn px-3 py-2 bg-white text-sm flex items-center gap-1 mb-3"><Plus size={16} weight="bold" />Neuer Rabattcode</button>

      <div className="space-y-2" data-testid="discount-list">
        {list.length === 0 && <p className="font-body text-sm text-gray-500">Noch keine Rabattcodes.</p>}
        {list.map((d) => (
          <div key={d.id} data-testid={`discount-${d.id}`} className="border-2 border-gray-800 rounded-2xl p-3 bg-white">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-heading font-bold text-gray-900">{d.code}</span>
              <span className="text-[11px] font-heading font-semibold px-2 py-0.5 rounded-full text-white" style={{ background: (STATUS[d.status] || {}).color }}>{(STATUS[d.status] || {}).label}</span>
              <span className="font-body text-sm text-gray-700">· {fmtVal(d)}</span>
              <div className="ml-auto flex items-center gap-1">
                <button data-testid={`send-discount-${d.id}`} onClick={() => { setSendOpen(d); setEmails(""); }} className="text-gray-600 p-1" title="Per E-Mail senden"><PaperPlaneTilt size={16} weight="bold" /></button>
                <button data-testid={`edit-discount-${d.id}`} onClick={() => openEdit(d)} className="text-gray-600 p-1"><PencilSimple size={16} weight="bold" /></button>
                <button data-testid={`delete-discount-${d.id}`} onClick={() => remove(d)} className="text-red-500 p-1"><Trash size={16} weight="bold" /></button>
              </div>
            </div>
            <p className="font-body text-xs text-gray-500 mt-1">
              {d.scope === "single" ? `Einmalnutzung (${d.email || "—"})` : d.scope === "multi" ? `Mehrfach (max. ${d.max_redemptions ?? "∞"})` : "Unbegrenzt"}
              {" · "}Einlösungen: {d.redemptions_count}
              {d.valid_to ? ` · Restlaufzeit: ${d.remaining_days != null ? (d.remaining_days >= 0 ? d.remaining_days + " Tage" : "abgelaufen") : "—"}` : ""}
            </p>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="discount-dialog" className="max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Rabattcode bearbeiten" : "Neuer Rabattcode"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Code (leer = automatisch)</label>
              <div className="flex gap-2 mt-1">
                <input data-testid="discount-code-input" className="sb-input flex-1" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="z. B. SOMMER10" />
                <button type="button" onClick={() => setForm({ ...form, code: "SB-" + Math.random().toString(36).slice(2, 8).toUpperCase() })} className="sb-btn px-3 bg-white text-xs flex items-center gap-1"><ArrowsClockwise size={14} weight="bold" />Generieren</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Rabatttyp</label>
                <select data-testid="discount-type" className="sb-input mt-1" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                  <option value="percent">Prozent (%)</option>
                  <option value="fixed">Fixer Betrag (€)</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Rabattwert</label>
                <input data-testid="discount-value" type="number" min="0" step="0.01" className="sb-input mt-1" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Gültig von</label>
                <input data-testid="discount-from" type="date" className="sb-input mt-1" value={form.valid_from} onChange={(e) => setForm({ ...form, valid_from: e.target.value })} />
              </div>
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Gültig bis</label>
                <input data-testid="discount-to" type="date" className="sb-input mt-1" value={form.valid_to} onChange={(e) => setForm({ ...form, valid_to: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Geltungsbereich</label>
              <select data-testid="discount-scope" className="sb-input mt-1" value={form.scope} onChange={(e) => setForm({ ...form, scope: e.target.value })}>
                <option value="single">Einmalnutzung (bestimmte E-Mail)</option>
                <option value="multi">Mehrfachnutzung (begrenzt, je Nutzer 1x)</option>
                <option value="unlimited">Unbegrenzt (alle, je Nutzer 1x)</option>
              </select>
            </div>
            {form.scope === "single" && (
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">E-Mail des Nutzers</label>
                <input data-testid="discount-email" type="email" className="sb-input mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
            )}
            {form.scope === "multi" && (
              <div>
                <label className="text-xs font-body text-gray-500 uppercase tracking-wider">Maximale Einlösungen</label>
                <input data-testid="discount-max" type="number" min="1" className="sb-input mt-1" value={form.max_redemptions} onChange={(e) => setForm({ ...form, max_redemptions: e.target.value })} />
              </div>
            )}
            <label className="flex items-center gap-2"><Switch data-testid="discount-active" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><span className="font-body text-sm">Aktiv</span></label>
            <button data-testid="save-discount-btn" disabled={busy} onClick={save} className="sb-btn w-full py-3 text-white font-medium disabled:opacity-60" style={{ background: "var(--app-primary)" }}>Speichern</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!sendOpen} onOpenChange={(o) => { if (!o) setSendOpen(null); }}>
        <DialogContent data-testid="discount-send-dialog">
          <DialogHeader><DialogTitle className="font-heading">Rabattcode senden {sendOpen ? `· ${sendOpen.code}` : ""}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="text-xs font-body text-gray-500 uppercase tracking-wider">E-Mail-Adressen (durch Komma/Zeilenumbruch getrennt)</label>
            <textarea data-testid="discount-send-emails" rows={4} className="sb-input" value={emails} onChange={(e) => setEmails(e.target.value)} placeholder="max@example.com, erika@example.com" />
            <button data-testid="discount-send-btn" disabled={busy} onClick={doSend} className="sb-btn w-full py-3 text-white font-medium disabled:opacity-60" style={{ background: "var(--app-primary)" }}>Senden</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
